/**
 * System Pulse tile — lightweight summary of alarm count + 1h error count
 * + 5min-stale outbox backlog. Links to the full System Health tab.
 *
 * Backend shape (buildSystemPulseTile):
 *   { active_alarms: number | null, errors_1h: number | null, outbox_backlog: number }
 *
 * `active_alarms` and `errors_1h` are currently null in the backend tile
 * (deferred to a parallel call from frontend OR Tier-2 enrichment). When
 * they're null we render "—"; once the backend populates them the same
 * component renders the numeric value.
 */
import { Link } from 'react-router-dom';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { formatNumber } from '@/lib/utils';

interface Data {
  active_alarms: number | null;
  errors_1h: number | null;
  outbox_backlog: number;
}

export function SystemPulseTile({ data }: { data: Data }) {
  const { hasPermission } = useAdminPermissions();
  const canDrillIn = hasPermission('analytics.system_health.read');

  const alarmText =
    data.active_alarms == null
      ? '—'
      : data.active_alarms === 0
        ? '✓ 0 alarms'
        : `⚠ ${data.active_alarms} active`;
  const alarmColor =
    data.active_alarms == null
      ? 'text-muted-foreground'
      : data.active_alarms === 0
        ? 'text-green-600'
        : 'text-red-600';

  const errorText = data.errors_1h == null ? '—' : `${formatNumber(data.errors_1h)} errors (1h)`;

  return (
    <div className="bg-card border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">System Pulse</div>
      <div className="mt-2 space-y-1 text-sm">
        <div className={alarmColor}>{alarmText}</div>
        <div className="text-muted-foreground">{errorText}</div>
        <div className="text-muted-foreground">
          Outbox backlog: {formatNumber(data.outbox_backlog)}
        </div>
      </div>
      {canDrillIn && (
        <Link to="/system-health" className="text-xs text-primary mt-3 inline-block">
          → System Health
        </Link>
      )}
    </div>
  );
}
